<template>
  <div>
    <main-layout>
    <template #header>
      <main-header :title="'AI 호출 이력' | translate" />
    </template>
    <template #feature>
      <div class="ai-feature-right">
        <v-btn
          elevation="0"
          color="#F9FCFF"
          class="ai-favorite-btn"
          @click="openFavoriteDialog"
        >
          <v-icon color="#0C98FE" left>mdi-star-outline</v-icon>
          자주 찾는 질문
        </v-btn>
      </div>
    </template>
    <template #toolbar>
      <main-toolbar>
        <search-bar
          :placeholder="'질문/템플릿 검색어를 입력하세요.' | translate"
          @update:keyword="onKeywordChanged"
          v-model="searchOptions.searchKeyword"
          style="width: 340px;"
        />
        <v-text-field
          v-model.number="searchOptions.rentalcompanyId"
          :label="'임대업체번호' | translate"
          type="number"
          dense
          outlined
          hide-details
          clearable
          style="width: 160px;"
        />
        <search-date-range-picker
          :label-for-start="'usage_day_kst 시작일' | translate"
          :label-for-end="'usage_day_kst 종료일' | translate"
          @update:range="onUsageDayRangeChanged"
          style="width: 320px"
        />
        <search-date-picker
          type="month"
          :label="'usage_month_kst 시작월' | translate"
          v-model="usageMonthFrom"
          :width="150"
        />
        <search-date-picker
          type="month"
          :label="'usage_month_kst 종료월' | translate"
          v-model="usageMonthTo"
          :width="150"
        />
        <search-single-select-box
          :items="sortFieldOptions"
          :label="'정렬 필드' | translate"
          v-model="searchOptions.sortBy[0]"
          style="width: 170px"
          :clearable="false"
        />
        <search-single-select-box
          :items="sortOrderOptions"
          :label="'정렬 방향' | translate"
          @update:selected="onSortOrderChanged"
          v-model="sortOrder"
          style="width: 130px"
          :clearable="false"
        />
        <v-btn
          color="primary"
          class="ml-2"
          @click="onSearchClick"
        >
          검색
        </v-btn>
      </main-toolbar>
    </template>

    <template #body>
      <main-body class="ai-call-history-body">
        <div
          ref="tableWrap"
          class="ai-call-history-table-wrap"
          :style="tableWrapStyle"
        >
          <vue-data-table
            ref="dataTable"
            :fetch="fetch"
            :headers="headers"
            item-key="raw.id"
            scroll-always-visible
            infinite-pagination
            :items-per-page="30"
            @click:row="onClickRow"
          />
        </div>
      </main-body>
    </template>

    </main-layout>

    <v-dialog v-model="resultDialog" max-width="1200">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">
          result_json
          <v-spacer />
          <v-btn icon small @click="resultDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <pre class="result-json-pre">{{ selectedResultJson }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="favoriteDialog" max-width="780">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">
          자주 찾는 질문 (최대 5건)
          <v-spacer />
          <v-btn icon small @click="favoriteDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-text-field
            v-for="(q, idx) in favoriteQuestions"
            :key="'fav-q-' + idx"
            v-model="favoriteQuestions[idx]"
            :label="`질문 ${idx + 1}`"
            outlined
            dense
            clearable
            class="mb-2"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-btn text @click="favoriteDialog = false">닫기</v-btn>
          <v-spacer />
          <v-btn depressed color="primary" @click="saveFavoriteQuestions">
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { PATH } from "@/constants";
import MainLayout from "@/components/common/layout/MainLayout";
import MainHeader from "@/components/common/layout/MainHeader";
import MainToolbar from "@/components/common/layout/MainToolbar";
import MainBody from "@/components/common/layout/MainBody";
import SearchBar from "@/components/input/SearchBar";
import SearchDateRangePicker from "@/components/input/SearchDateRangePicker";
import SearchDatePicker from "@/components/input/SearchDatePicker";
import SearchSingleSelectBox from "@/components/input/SearchSingleSelectBox";
import VueDataTable from "@/assets/plugins/vue-data-table/VueDataTable";
import Board from "@/model/board";

export default {
  name: "AiCallHistoryList",
  components: {
    MainLayout,
    MainHeader,
    MainToolbar,
    MainBody,
    SearchBar,
    SearchDateRangePicker,
    SearchDatePicker,
    SearchSingleSelectBox,
    VueDataTable,
  },
  data() {
    return {
      headers: [
        { text: "ID", value: "raw.id", width: 80 },
        { text: "임대업체", value: "raw.rentalcompanyId", width: 110 },
        { text: "사용자", value: "raw.userId", width: 90 },
        { text: "질문", value: "raw.question", width: 360 },
        { text: "template", value: "raw.template", width: 140 },
        { text: "rowCount", value: "raw.rowCount", width: 90 },
        { text: "latency(ms)", value: "raw.latency", width: 110 },
        { text: "prompt", value: "raw.openaiPromptTokens", width: 90 },
        { text: "completion", value: "raw.openaiCompletionTokens", width: 110 },
        { text: "total", value: "raw.openaiTotalTokens", width: 90 },
        { text: "credit", value: "raw.creditUsed", width: 90 },
        { text: "usage_day_kst", value: "raw.usageDayKst", width: 130 },
        { text: "usage_month_kst", value: "raw.usageMonthKst", width: 130 },
        { text: "hide", value: "raw.hideFromRecentYn", width: 70 },
        { text: "created_at", value: "raw.createdAt", width: 170 },
      ],
      searchOptions: {
        searchKeyword: null,
        rentalcompanyId: null,
        usageDayFrom: null,
        usageDayTo: null,
        usageMonthFrom: null,
        usageMonthTo: null,
        sortBy: ["createdAt"],
        sortDesc: [true],
      },
      usageMonthFrom: null,
      usageMonthTo: null,
      sortOrder: "desc",
      resultDialog: false,
      selectedResultJson: "",
      favoriteDialog: false,
      favoriteQuestions: ["", "", "", "", ""],
      tableWrapHeightPx: null,
      sortFieldOptions: [
        { codeKey: "createdAt", codeValue: "생성일시" },
        { codeKey: "id", codeValue: "ID" },
        { codeKey: "rentalcompanyId", codeValue: "임대업체번호" },
        { codeKey: "usageDayKst", codeValue: "usage_day_kst" },
        { codeKey: "usageMonthKst", codeValue: "usage_month_kst" },
        { codeKey: "openaiTotalTokens", codeValue: "total tokens" },
        { codeKey: "latency", codeValue: "latency" },
      ],
      sortOrderOptions: [
        { codeKey: "desc", codeValue: "내림차순" },
        { codeKey: "asc", codeValue: "오름차순" },
      ],
    };
  },
  created() {
    this.loadFavoriteQuestions();
  },
  mounted() {
    this.syncTableWrapHeight();
    window.addEventListener("resize", this.syncTableWrapHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.syncTableWrapHeight);
  },
  computed: {
    tableWrapStyle() {
      if (this.tableWrapHeightPx == null) {
        return {};
      }
      const h = `${this.tableWrapHeightPx}px`;
      return { height: h, maxHeight: h };
    },
  },
  methods: {
    syncTableWrapHeight() {
      this.$nextTick(() => {
        const el = this.$refs.tableWrap;
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        const height = Math.floor(window.innerHeight * 0.95 - top);
        this.tableWrapHeightPx = Math.max(240, height);
      });
    },
    async fetch(pageNumber, pageSize) {
      this.searchOptions.usageMonthFrom = this.toYearMonth(this.usageMonthFrom);
      this.searchOptions.usageMonthTo = this.toYearMonth(this.usageMonthTo);
      const res = await this.$request(PATH.AI_MANAGE.CALL_HISTORY_LIST)
        .post()
        .setObject({
          ...this.searchOptions,
          pageNumber,
          pageSize,
        })
        .catch()
        .enqueue();
      return (res.data || []).map((e) => new Board(e));
    },
    onUsageDayRangeChanged(range) {
      this.searchOptions.usageDayFrom = range.start;
      this.searchOptions.usageDayTo = range.end;
    },
    onKeywordChanged() {},
    onSortOrderChanged(order) {
      this.sortOrder = order || "desc";
      this.searchOptions.sortDesc = [this.sortOrder !== "asc"];
    },
    onSearchClick() {
      this.onFilterChanged();
    },
    onFilterChanged() {
      const table = this.$refs.dataTable;
      if (table && typeof table.clear === "function") {
        table.clear();
      }
    },
    toYearMonth(isoDateTime) {
      if (!isoDateTime) return null;
      const s = String(isoDateTime);
      return s.length >= 7 ? s.substring(0, 7) : null;
    },
    onClickRow(item) {
      const raw = item && item.raw ? item.raw : {};
      const text = raw.resultJson;
      if (!text) {
        this.selectedResultJson = "(result_json 없음)";
        this.resultDialog = true;
        return;
      }
      try {
        this.selectedResultJson = JSON.stringify(JSON.parse(text), null, 2);
      } catch (_) {
        this.selectedResultJson = String(text);
      }
      this.resultDialog = true;
    },
    async loadFavoriteQuestions() {
      try {
        const res = await this.$request(PATH.AI_MANAGE.FAVORITE_QUESTIONS_LIST)
          .get()
          .catch()
          .enqueue();
        const list = Array.isArray(res && res.questions) ? res.questions : [];
        this.favoriteQuestions = [0, 1, 2, 3, 4].map((idx) => list[idx] || "");
      } catch (e) {
        console.warn("favorite questions load", e);
      }
    },
    openFavoriteDialog() {
      this.loadFavoriteQuestions();
      this.favoriteDialog = true;
    },
    async saveFavoriteQuestions() {
      try {
        const payload = this.favoriteQuestions
          .map((q) => (q || "").trim())
          .filter((q) => q.length > 0)
          .slice(0, 5);
        await this.$request(PATH.AI_MANAGE.FAVORITE_QUESTIONS_REPLACE)
          .post()
          .setObject({ questions: payload })
          .catch()
          .enqueue();
        this.favoriteDialog = false;
      } catch (e) {
        console.warn("favorite questions save", e);
      }
    },
  },
};
</script>

<style scoped>
.ai-call-history-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.ai-call-history-table-wrap {
  min-height: 0;
  overflow: hidden;
}

.ai-call-history-table-wrap >>> .vue-data-table {
  height: 100%;
}

.result-json-pre {
  max-height: 65vh;
  overflow: auto;
  background: #f7f9fc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-favorite-btn {
  border: 1px solid rgba(12, 152, 254, 0.35);
}

.ai-feature-right {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}
</style>

